import { useState, useCallback, useMemo } from 'react';
import { FaChevronDown, FaChevronRight, FaCheckSquare, FaRegSquare, FaFileInvoiceDollar, FaMagic } from 'react-icons/fa';
import { Auth } from 'aws-amplify';
import BadgeStatus from '../../atoms/BadgeStatus';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter } from '../../molecules/FilterBuilder/types';
import { listInvoicesByOperation } from '../billing/services/billingApi';
import { operationsFilterSchema, serializeFilters } from './constants/filterSchemas';
import ReceivingBankAccountSelect from './components/ReceivingBankAccountSelect';
import appConfig from '../../../config/app-config';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Invoice {
    id: number;
    invoiceId: string;
    chargeId: string | null;
    creditOperationId: string;
    amount: number;
    amountPaid: number | null;
    dueDate: string;
    status: string;
    statusFormatted?: string;
    collect?: boolean;
    documentTitle?: string | null;
    installmentNumber?: number | null;
    payer?: { name: string; documentNumber: string | null };
}

interface ContractGroup {
    operation_number: string;
    credit_operation_id: string;
    operation_type: string;
    approved_value: number;
    participants: any[];
    invoices: Invoice[];
    total_open: number;
    expanded: boolean;
    selected: boolean;
    invoicesLoaded: boolean;
}

interface PaymentInfo {
    amount: string;
    date: string;
    payer_name: string;
    payer_document: string;
    receiving_bank_account_id: string;
    payment_method: string;
    notes: string;
}

// ─── Steps ──────────────────────────────────────────────────────────────────

const STEPS = ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Faturas'] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

const invoiceBalance = (inv: Invoice) => Number(inv.amount) - Number(inv.amountPaid || 0);

const fetchInvoices = async (creditOpId: string): Promise<Invoice[]> => {
    const data = await listInvoicesByOperation(creditOpId);
    const list: Invoice[] = data.list || [];
    return list.filter(inv => inv.status === 'OPEN' || inv.status === 'PARTIALLY_PAID');
};

const modalityLabels: Record<string, string> = {
    LOAN: 'Empréstimo', CESSION: 'Cessão', COMMON_CPRF: 'CPRF',
    CESSION_WITHOUT_RETURN: 'Cessão s/ regresso', CESSION_WITH_RETURN: 'Cessão c/ regresso',
};

// ─── Main Component ─────────────────────────────────────────────────────────

const RegisterPaymentPage = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [payment, setPayment] = useState<PaymentInfo>({
        amount: '', date: new Date().toISOString().split('T')[0],
        payer_name: '', payer_document: '', receiving_bank_account_id: '',
        payment_method: 'FINANCIAL', notes: '',
    });

    // Step 2: Contracts (FilterBuilder)
    const [searchResults, setSearchResults] = useState<ContractGroup[]>([]);
    const [selectedContracts, setSelectedContracts] = useState<ContractGroup[]>([]);
    const [loading, setLoading] = useState(false);

    // Step 3: Invoices
    const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);

    const { address: SETTLEMENT_URL, key: SETTLEMENT_KEY } = appConfig.apis.settlementV2;
    const paymentAmount = parseFloat(payment.amount.replace(/\./g, '').replace(',', '.')) || 0;

    // ─── Step 1: Payment info ────────────────────────────────────────────────
    const updatePayment = (field: keyof PaymentInfo, value: string) => {
        setPayment(prev => ({ ...prev, [field]: value }));
    };

    // ─── Step 2: Contract search via FilterBuilder ───────────────────────────
    const searchOperations = useCallback(async (filters: AppliedFilter[]) => {
        setLoading(true);
        try {
            const params = serializeFilters(filters);
            params.set('page', '1');
            params.set('size', '50');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${SETTLEMENT_URL}/operations?${params.toString()}`, {
                headers: { 'x-api-key': SETTLEMENT_KEY, 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const ops = data.list || data.data?.list || [];
            setSearchResults(ops.map((op: any) => ({
                operation_number: op.contract_number || op.operation_number || '-',
                credit_operation_id: op.credit_operation_id,
                operation_type: op.contract_modality || '',
                approved_value: Number(op.total_amount || 0),
                participants: [],
                invoices: [], total_open: Number(op.total_open || 0),
                expanded: false,
                selected: !!selectedContracts.find(sc => sc.credit_operation_id === op.credit_operation_id),
                invoicesLoaded: false,
            })));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [selectedContracts, SETTLEMENT_URL, SETTLEMENT_KEY]);

    const toggleContractExpand = async (idx: number) => {
        const contract = searchResults[idx];
        if (!contract.invoicesLoaded) {
            try {
                const invoices = await fetchInvoices(contract.credit_operation_id);
                const totalOpen = invoices.reduce((s, inv) => s + invoiceBalance(inv), 0);
                setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, invoices, total_open: totalOpen, expanded: true, invoicesLoaded: true } : c));
                setSelectedContracts(prev => prev.map(c => c.credit_operation_id === contract.credit_operation_id ? { ...c, invoices, total_open: totalOpen, invoicesLoaded: true } : c));
            } catch (e) { console.error(e); }
        } else {
            setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, expanded: !c.expanded } : c));
        }
    };

    const toggleContractSelect = (idx: number) => {
        const contract = searchResults[idx];
        const exists = selectedContracts.find(c => c.credit_operation_id === contract.credit_operation_id);
        if (exists) {
            setSelectedContracts(prev => prev.filter(c => c.credit_operation_id !== contract.credit_operation_id));
        } else {
            setSelectedContracts(prev => [...prev, { ...contract, selected: true }]);
        }
        setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, selected: !c.selected } : c));
    };

    const removeSelectedContract = (id: string) => {
        setSelectedContracts(prev => prev.filter(c => c.credit_operation_id !== id));
        setSearchResults(prev => prev.map(c => c.credit_operation_id === id ? { ...c, selected: false } : c));
    };

    // ─── Step 3: Smart invoice selection ─────────────────────────────────────
    const goToStep3 = async () => {
        setLoading(true);
        try {
            const updated: ContractGroup[] = [];
            for (const c of selectedContracts) {
                if (c.invoicesLoaded) { updated.push(c); }
                else {
                    const invoices = await fetchInvoices(c.credit_operation_id);
                    const totalOpen = invoices.reduce((s, inv) => s + invoiceBalance(inv), 0);
                    updated.push({ ...c, invoices, total_open: totalOpen, invoicesLoaded: true });
                }
            }
            setSelectedContracts(updated);
            const allInvoices = updated.flatMap(c => c.invoices).sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
            autoSelectInvoices(allInvoices);
            setCurrentStep(2);
        } finally { setLoading(false); }
    };

    const autoSelectInvoices = (invoices: Invoice[]) => {
        let remaining = paymentAmount;
        const selected: Invoice[] = [];
        for (const inv of invoices) {
            if (remaining <= 0) break;
            selected.push(inv);
            remaining -= invoiceBalance(inv);
        }
        setSelectedInvoices(selected);
    };

    const toggleInvoiceSelect = (inv: Invoice) => {
        setSelectedInvoices(prev =>
            prev.find(i => i.id === inv.id) ? prev.filter(i => i.id !== inv.id) : [...prev, inv]
        );
    };

    const isInvoiceSelected = (inv: Invoice) => !!selectedInvoices.find(i => i.id === inv.id);

    const totalSelected = useMemo(() =>
        selectedInvoices.reduce((sum, inv) => sum + invoiceBalance(inv), 0), [selectedInvoices]);

    const diff = totalSelected - paymentAmount;

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FaFileInvoiceDollar className="text-green-600" />
                    Registrar Recebimento
                </h1>
                <p className="text-sm text-gray-500 mt-1">Baixa em lote de faturas (cobrança)</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center mb-8 bg-white rounded-lg p-4 shadow-sm">
                {STEPS.map((step, idx) => (
                    <div key={step} className="flex items-center flex-1">
                        <div className={`flex items-center gap-2 ${idx <= currentStep ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < currentStep ? 'bg-green-500 text-white' : idx === currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {idx < currentStep ? '✓' : idx + 1}
                            </span>
                            <span className="text-sm">{step}</span>
                        </div>
                        {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />}
                    </div>
                ))}
            </div>

            {/* ═══ STEP 1: Payment Info ═══ */}
            {currentStep === 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 mb-4">Informações do Recebimento</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Valor Recebido (R$) *</label>
                            <input type="text" value={payment.amount}
                                onChange={e => updatePayment('amount', e.target.value)}
                                placeholder="2.500.000,00"
                                className="w-full border rounded px-3 py-2 text-lg font-semibold" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Data do Pagamento *</label>
                            <input type="date" value={payment.date}
                                onChange={e => updatePayment('date', e.target.value)}
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">CPF/CNPJ de quem pagou *</label>
                            <input type="text" value={payment.payer_document}
                                onChange={e => updatePayment('payer_document', e.target.value)}
                                placeholder="00.000.000/0001-00"
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Nome de quem pagou</label>
                            <input type="text" value={payment.payer_name}
                                onChange={e => updatePayment('payer_name', e.target.value)}
                                placeholder="Será buscado pelo CPF/CNPJ"
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Conta de Recebimento *</label>
                            <ReceivingBankAccountSelect
                                value={payment.receiving_bank_account_id}
                                onChange={v => updatePayment('receiving_bank_account_id', v)}
                                className="w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Forma de Recebimento *</label>
                            <select value={payment.payment_method}
                                onChange={e => updatePayment('payment_method', e.target.value)}
                                className="w-full border rounded px-3 py-2">
                                <option value="FINANCIAL">FINANCEIRO (TED/PIX)</option>
                                <option value="BOLETO">BOLETO</option>
                                <option value="DEBT_CONFESSION">CONFISSÃO DE DÍVIDA</option>
                                <option value="ASSET_DATION">DAÇÃO (Grãos/Veículo/Imóvel)</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Observações</label>
                            <input type="text" value={payment.notes}
                                onChange={e => updatePayment('notes', e.target.value)}
                                placeholder="Referência bancária, NF, etc."
                                className="w-full border rounded px-3 py-2" />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            disabled={!payment.amount || !payment.date || !payment.payer_document}
                            onClick={() => setCurrentStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                            Avançar →
                        </button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 2: Select Contracts (FilterBuilder) ═══ */}
            {currentStep === 1 && (
                <div className="bg-white rounded-lg shadow-sm">
                    {/* FilterBuilder */}
                    <div className="p-4 border-b">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-gray-800">Buscar Operações de Crédito</h2>
                            <div className="text-sm font-semibold text-green-700">Valor: R$ {fmt(paymentAmount)}</div>
                        </div>
                        <FilterBuilder
                            schema={operationsFilterSchema}
                            onSearch={searchOperations}
                            loading={loading}
                        />
                    </div>

                    {/* Selected contracts chips */}
                    {selectedContracts.length > 0 && (
                        <div className="px-4 py-3 bg-blue-50 border-b">
                            <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {selectedContracts.map(c => (
                                    <span key={c.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded text-sm">
                                        {c.operation_number}
                                        <button onClick={() => removeSelectedContract(c.credit_operation_id)} className="text-gray-400 hover:text-red-500 ml-1">&times;</button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Results */}
                    {searchResults.length > 0 ? (
                        <div className="divide-y max-h-[50vh] overflow-y-auto">
                            {searchResults.map((contract, idx) => (
                                <div key={contract.credit_operation_id}>
                                    <div className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => toggleContractExpand(idx)}>
                                        <button className="mr-3" onClick={e => { e.stopPropagation(); toggleContractSelect(idx); }}>
                                            {contract.selected ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}
                                        </button>
                                        <span className="mr-2 text-gray-400">{contract.expanded ? <FaChevronDown /> : <FaChevronRight />}</span>
                                        <div className="flex-1">
                                            <span className="font-medium">{contract.operation_number}</span>
                                            <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded">{modalityLabels[contract.operation_type] || contract.operation_type}</span>
                                        </div>
                                        <span className="text-sm font-medium text-green-700">
                                            {contract.invoicesLoaded ? `R$ ${fmt(contract.total_open)} em aberto` : `R$ ${fmt(contract.approved_value)}`}
                                        </span>
                                    </div>
                                    {contract.expanded && contract.invoices.length > 0 && (
                                        <div className="bg-gray-50 px-12 py-2 text-xs text-gray-500">
                                            {contract.invoices.slice(0, 5).map(inv => (
                                                <div key={inv.id} className="flex justify-between py-0.5">
                                                    <span>{inv.payer?.name || (inv.chargeId ? `Recebível ${inv.chargeId.slice(0, 8)}` : inv.documentTitle) || `Parcela ${inv.installmentNumber}`}</span>
                                                    <span>R$ {fmt(invoiceBalance(inv))}</span>
                                                </div>
                                            ))}
                                            {contract.invoices.length > 5 && <div className="text-center mt-1">+{contract.invoices.length - 5} faturas...</div>}
                                        </div>
                                    )}
                                    {contract.expanded && contract.invoicesLoaded && contract.invoices.length === 0 && (
                                        <div className="bg-gray-50 px-12 py-2 text-xs text-gray-400">Nenhuma fatura em aberto</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-400">Use os filtros acima para buscar operações</div>
                    )}

                    <div className="p-4 border-t flex items-center justify-between bg-gray-50">
                        <span className="text-sm text-gray-600">{selectedContracts.length} contrato(s) selecionado(s)</span>
                        <div className="flex gap-3">
                            <button onClick={() => setCurrentStep(0)} className="px-4 py-2 border rounded-lg hover:bg-gray-100">← Voltar</button>
                            <button disabled={selectedContracts.length === 0 || loading} onClick={goToStep3}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                {loading ? 'Carregando...' : 'Avançar →'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ STEP 3: Select Invoices (smart) ═══ */}
            {currentStep === 2 && (
                <div className="bg-white rounded-lg shadow-sm">
                    {/* Summary bar */}
                    <div className="p-4 border-b flex items-center justify-between">
                        <div>
                            <span className="text-sm text-gray-600">Valor do recebimento:</span>
                            <span className="ml-2 font-bold text-lg">R$ {fmt(paymentAmount)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <div className="text-sm text-gray-500">Selecionado: <span className="font-semibold text-green-700">R$ {fmt(totalSelected)}</span></div>
                                <div className={`text-xs ${diff > 0 ? 'text-red-500' : diff < 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                                    {diff === 0 ? '✓ Exato' : diff > 0 ? `↑ R$ ${fmt(diff)} excedente` : `↓ R$ ${fmt(Math.abs(diff))} restante`}
                                </div>
                            </div>
                            <button onClick={() => { const all = selectedContracts.flatMap(c => c.invoices).sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || '')); autoSelectInvoices(all); }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-sm">
                                <FaMagic /> Auto-selecionar
                            </button>
                        </div>
                    </div>

                    <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {selectedContracts.map(contract => (
                            <div key={contract.credit_operation_id}>
                                <div className="flex items-center px-4 py-2 bg-gray-50 border-b">
                                    <span className="font-semibold text-gray-800">{contract.operation_number}</span>
                                    <span className="ml-3 text-xs text-gray-500">
                                        {contract.invoices.filter(inv => isInvoiceSelected(inv)).length} / {contract.invoices.length} selecionadas
                                    </span>
                                </div>
                                <div className="px-4">
                                    <table className="w-full text-sm">
                                        <thead><tr className="text-xs text-gray-400">
                                            <th className="w-8"></th>
                                            <th className="text-left py-1">Pagador</th>
                                            <th className="text-left">Recebível</th>
                                            <th className="text-right">Saldo</th>
                                            <th className="text-right">Vencimento</th>
                                            <th className="text-center">Status</th>
                                        </tr></thead>
                                        <tbody>
                                            {contract.invoices.map(inv => (
                                                <tr key={inv.id} className={`border-t border-gray-100 cursor-pointer ${isInvoiceSelected(inv) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                    onClick={() => toggleInvoiceSelect(inv)}>
                                                    <td className="py-1.5">
                                                        {isInvoiceSelected(inv) ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}
                                                    </td>
                                                    <td className="truncate max-w-[200px]">{inv.payer?.name || '-'}</td>
                                                    <td className="text-gray-500 font-mono text-xs">{inv.chargeId ? inv.chargeId.slice(0, 8) : (inv.documentTitle || '-')}</td>
                                                    <td className="text-right font-medium">R$ {fmt(invoiceBalance(inv))}</td>
                                                    <td className="text-right text-gray-500">{inv.dueDate?.split('-').reverse().join('/') || '-'}</td>
                                                    <td className="text-center">
                                                        <BadgeStatus text={inv.statusFormatted || inv.status} color={inv.status === 'PARTIALLY_PAID' ? 'orange' : 'yellow'} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-4 border-t flex items-center justify-between bg-gray-50">
                        <div>
                            <span className="text-sm">{selectedInvoices.length} faturas</span>
                            <span className="ml-2 font-bold text-green-700">R$ {fmt(totalSelected)}</span>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setCurrentStep(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-100">← Voltar</button>
                            <button disabled className="px-6 py-2 bg-green-600 text-white rounded-lg opacity-50 cursor-not-allowed"
                                title="Persistência: endpoint register-invoice-payment (billing) com payload {invoices[], receiving_bank_account_id, total_payment, payment_method, payment_type}">
                                Confirmar Recebimento (em breve)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterPaymentPage;
